import { useParams, useNavigate } from "react-router";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { useEncryption } from "@/hooks/useEncryption";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { EmptyState } from "@/components/shared/EmptyState";
import { MarkdownText } from "@/components/shared/MarkdownText";
import { PointsChart } from "@/components/charts/PointsChart";
import { useState, useEffect } from "react";
import { ChevronDown, ChevronRight, Palmtree, History, UserCog } from "lucide-react";

interface BatchItem {
  item_id: string;
  item_type: string;
  points: number;
  name: string;
  icon: string;
}

interface HistoryItem {
  type: string;
  points: number;
  created_at: string;
  entity_name: string;
  entity_icon: string;
  encrypted_note?: string;
  ref_name?: string;
  ref_icon?: string;
  streak_days?: number;
  streak_factor?: number;
  items?: BatchItem[];
}

function getEventStyle(type: string) {
  switch (type) {
    case "rule_broken":
      return { emoji: "⚠️", bg: "bg-app-danger/10", border: "border-l-app-danger", text: "text-app-danger" };
    case "chore_completed":
      return { emoji: "✅", bg: "bg-app-success/10", border: "border-l-app-success", text: "text-app-success" };
    case "chore_overdue":
      return { emoji: "⏰", bg: "bg-app-warning/10", border: "border-l-app-warning", text: "text-app-warning" };
    case "rule_streak":
      return { emoji: "🔥", bg: "bg-app-info/10", border: "border-l-app-info", text: "text-app-info" };
    case "punishment_redeemed":
      return { emoji: "💀", bg: "bg-app-accent/10", border: "border-l-app-accent", text: "text-app-accent" };
    case "reward_redeemed":
      return { emoji: "🎁", bg: "bg-app-accent/10", border: "border-l-app-accent", text: "text-app-accent" };
    case "session_purchase":
      return { emoji: "🛒", bg: "bg-app-accent/10", border: "border-l-app-accent", text: "text-app-accent" };
    case "session_purchase_reversed":
      return { emoji: "↩️", bg: "bg-app-surface-raised", border: "border-l-app-border", text: "text-app-text-muted" };
    case "punishment_reversed":
    case "reward_reversed":
      return { emoji: "↩️", bg: "bg-app-surface-raised", border: "border-l-app-border", text: "text-app-text-muted" };
    default:
      return { emoji: "📝", bg: "bg-app-surface-raised", border: "border-l-app-border", text: "text-app-text-muted" };
  }
}

function formatHistoryLabel(h: HistoryItem): string {
  const name = h.entity_name || "[Encrypted]";
  switch (h.type) {
    case "chore_completed": return `Completed ${name}`;
    case "chore_overdue": return `Overdue: ${name}`;
    case "rule_broken": return `Rule broken: ${name}${h.encrypted_note ? ` — ${h.encrypted_note}` : ""}`;
    case "rule_streak": return `${name} streak: ${h.streak_days}d (×${h.streak_factor})`;
    case "punishment_redeemed": return `Punishment: ${h.ref_name || name}`;
    case "reward_redeemed": return `Reward: ${h.ref_name || name}`;
    case "punishment_reversed": return `Reversed punishment: ${h.ref_name || name}`;
    case "reward_reversed": return `Reversed reward: ${h.ref_name || name}`;
    case "session_purchase": {
      const count = h.items?.length || 0;
      return `Session purchase (${count} item${count !== 1 ? "s" : ""})`;
    }
    case "session_purchase_reversed": {
      const count = h.items?.length || 0;
      return `Purchase reversed (${count} item${count !== 1 ? "s" : ""})`;
    }
    default: return h.type?.replace(/_/g, " ") || "Event";
  }
}

function getItemTypeLabel(itemType: string): string {
  switch (itemType) {
    case "reward_redeemed": return "Reward";
    case "punishment_redeemed": return "Punishment";
    case "reward_reversed": return "Reward reversed";
    case "punishment_reversed": return "Punishment reversed";
    default: return itemType.replace(/_/g, " ");
  }
}

export function ClientDashboard() {
  const { jwt } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { decryptValue, hasKey } = useEncryption(user?._key);
  const { data, loading, error } = useApi<{
    client: { name: string; icon: string };
    points: number;
    history: HistoryItem[];
    admin: { name: string; icon: string; description: string } | null;
    holiday?: { active: boolean; endsAt: string | null; startedBy: string | null; adminHoliday: boolean; adminEndsAt?: string | null };
  }>(`/api/client/${jwt}/dashboard`);

  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [expandedBlocks, setExpandedBlocks] = useState<Set<number>>(new Set());

  const toggleExpand = (index: number) => {
    setExpandedBlocks(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  // Decrypt history entity names + batch item names
  useEffect(() => {
    if (!data?.history) return;
    (async () => {
      const decrypted = await Promise.all(
        data.history.map(async (h) => ({
          ...h,
          entity_name: h.entity_name ? await decryptValue(h.entity_name) : "",
          ref_name: h.ref_name ? await decryptValue(h.ref_name) : undefined,
          encrypted_note: h.encrypted_note ? await decryptValue(h.encrypted_note) : undefined,
          items: h.items
            ? await Promise.all(
                h.items.map(async (item) => ({
                  ...item,
                  name: item.name ? await decryptValue(item.name) : "",
                })),
              )
            : undefined,
        })),
      );
      setHistory(decrypted);
    })();
  }, [data?.history, decryptValue]);

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-app-danger text-body p-4">{error}</p>;

  const points = data?.points ?? 0;
  const recentActivity = history.slice(0, 10);

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Holiday Banner */}
      {data?.holiday?.active && (
        <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 py-0">
          <CardContent className="flex items-center gap-3 p-4">
            <Palmtree className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-200">
                🏖️ Holiday mode is active
              </p>
              <p className="text-sm text-amber-600 dark:text-amber-400">
                Chores, rules, and notifications are paused.
                {data.holiday.adminHoliday && data.holiday.adminEndsAt && (
                  <> Ends {new Date(data.holiday.adminEndsAt).toLocaleDateString()} (set by admin)</>
                )}
                {!data.holiday.adminHoliday && data.holiday.endsAt && (
                  <> Ends {new Date(data.holiday.endsAt).toLocaleDateString()}</>
                )}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Points Card */}
      <Card className="border-0 shadow-lg overflow-hidden py-0">
        <div className="bg-linear-to-br from-app-primary/20 via-app-accent/10 to-transparent p-6 md:p-8 flex flex-col items-center text-center">
          {/* Client profile row */}
          <button
            onClick={() => navigate(`/client/${jwt}/profile`)}
            className="flex items-center gap-3 mb-4 px-4 py-2.5 rounded-xl bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors w-full max-w-xs cursor-pointer"
          >
            <span className="text-3xl shrink-0">{data?.client?.icon}</span>
            <div className="min-w-0 text-left flex-1">
              <p className="text-sm font-bold text-app-text truncate">{data?.client?.name}</p>
              <p className="text-xs text-app-text-muted">Your Profile</p>
            </div>
            <UserCog className="w-4 h-4 text-app-text-muted shrink-0" />
          </button>

          <p className="text-sm font-medium text-app-text-muted mb-3">Your Points</p>
          <div className={`w-28 h-28 rounded-full flex items-center justify-center shadow-lg ${points >= 0 ? "bg-app-success/20" : "bg-app-danger/20"}`}>
            <span className={`text-5xl font-extrabold ${points >= 0 ? "text-app-success" : "text-app-danger"}`}>
              {points}
            </span>
          </div>
          <div className="flex gap-1.5 mt-4 text-xl">
            <span className="animate-pulse">⭐</span>
            <span className="animate-pulse delay-200">💎</span>
            <span className="animate-pulse delay-400">⭐</span>
          </div>
          {data?.admin && (
            <div className="flex items-center gap-3 mt-5 px-4 py-2.5 rounded-xl bg-white/10 backdrop-blur-sm">
              <span className="text-3xl shrink-0">{data.admin.icon}</span>
              <div className="min-w-0 text-left">
                <p className="text-xs font-medium text-app-text-muted">Managed by</p>
                <p className="text-sm font-bold text-app-text truncate">{data.admin.name}</p>
                {data.admin.description && (
                  <MarkdownText text={data.admin.description} className="text-xs text-app-text-muted truncate" plain />
                )}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Points Chart */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="text-base text-app-text-muted">📈 Points Over Time</CardTitle>
        </CardHeader>
        <CardContent>
          <PointsChart history={history} height={200} />
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base text-app-text-muted">🕐 Recent Activity</CardTitle>
          <Button variant="ghost" size="sm" className="text-xs text-app-text-muted gap-1" onClick={() => navigate(`/client/${jwt}/activity`)}>
            <History className="w-3.5 h-3.5" /> View All
          </Button>
        </CardHeader>
        <CardContent>
          {recentActivity.length === 0 ? (
            <EmptyState
              icon="📭"
              title="No activity yet"
              description="Complete some chores to start earning points!"
            />
          ) : (
            <div className="space-y-2">
              {recentActivity.map((h, i) => {
                const style = getEventStyle(h.type);
                const isBatch = h.type === "session_purchase" || h.type === "session_purchase_reversed";
                const isExpanded = expandedBlocks.has(i);
                return (
                  <div key={i}>
                    <div
                      className={`flex items-center gap-3 p-2.5 rounded-lg border-l-4 ${style.border} ${style.bg} hover:opacity-90 transition-opacity ${isBatch && h.items?.length ? "cursor-pointer" : ""}`}
                      onClick={() => isBatch && h.items?.length && toggleExpand(i)}
                    >
                      {isBatch && h.items?.length ? (
                        <span className="text-lg shrink-0">
                          {isExpanded ? <ChevronDown className="w-4 h-4 inline" /> : <ChevronRight className="w-4 h-4 inline" />}
                        </span>
                      ) : (
                        <span className="text-lg shrink-0">{style.emoji}</span>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-app-text truncate">{formatHistoryLabel(h)}</p>
                        <p className="text-xs text-app-text-muted">
                          {new Date(h.created_at).toLocaleDateString()} · {new Date(h.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                      <span className={`text-sm font-bold shrink-0 ${h.points >= 0 ? "text-app-success" : "text-app-danger"}`}>
                        {h.points >= 0 ? "+" : ""}{h.points}
                      </span>
                    </div>
                    {isBatch && isExpanded && h.items && h.items.length > 0 && (
                      <div className="ml-6 mt-1 space-y-1">
                        {h.items.map((item, j) => (
                          <div key={j} className="flex items-center gap-2 p-1.5 rounded-md bg-app-surface-raised/50 text-xs">
                            <span className="text-base shrink-0">{item.icon}</span>
                            <span className="flex-1 min-w-0 truncate text-app-text">{item.name || "[Encrypted]"}</span>
                            <span className="text-app-text-muted">{getItemTypeLabel(item.item_type)}</span>
                            <span className={`font-semibold shrink-0 ${item.points >= 0 ? "text-app-success" : "text-app-danger"}`}>
                              {item.points >= 0 ? "+" : ""}{item.points}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
